import { Button, TextField, Box, Checkbox } from "@mui/material";
import { useState, type ChangeEvent } from "react";
import { createDisasterManualTrigger } from "../../api";
import SsSelect from "./SsSelect";
import PrefectureSelect from "../../components/PrefectureSelect";
import DivisionSelect from "./DivisionSelect";
import SnackbarCustom, {
  type SnackbarData,
} from "../../components/SnackBarCustom";
import PopoverCustom from "../../components/PopoverCustom";
import DatePickerJapanese from "../../components/DatePickerJapanese";
import { getCurrentDate } from "../../utils/getCurrentDate";
import type { CreateDisasterManualTriggerPayload } from "../../types/disaster";

type ManualRegistrationProps = {
  onRegistrationSuccess?: () => void;
};

const ManualRegistration = ({
  onRegistrationSuccess,
}: ManualRegistrationProps) => {
  const [postContents, setPostContents] =
    useState<CreateDisasterManualTriggerPayload>({
      disaster_id: null,
      pref_code: null,
      m_division_code: null,
      site_id: null,
      append_text: "",
      scheduled_at: null,
      is_attached_file: 1,
    });

  const handleChangePref = (value: string[]) => {
    setPostContents((prev) => ({
      ...prev,
      pref_code: value ? value : null,
    }));
  };

  const handleChangeDivision = (value: string | null) => {
    setPostContents((prev) => ({
      ...prev,
      m_division_code: value ? value : null,
    }));
  };

  const handleChangeSites = (value: number[]) => {
    setPostContents((prev) => ({
      ...prev,
      site_id: value.length > 0 ? value : null,
    }));
  };

  const handleChangeAdditionalText = (e: ChangeEvent<HTMLInputElement>) => {
    setPostContents((prev) => ({
      ...prev,
      append_text: e.target.value,
    }));
  };

  const handleChangeImmediate = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setPostContents((prev) => ({
        ...prev,
        scheduled_at: null,
      }));
    } else {
      setPostContents((prev) => ({
        ...prev,
        scheduled_at: getCurrentDate(),
      }));
    }
  };

  const handleChangeScheduledAt = (e: Date | null) => {
    if (e === null) {
      setPostContents((prev) => ({
        ...prev,
        scheduled_at: null,
      }));
      return;
    }

    const year = e.getFullYear();
    const month = (e.getMonth() + 1).toString().padStart(2, "0");
    const day = e.getDate().toString().padStart(2, "0");
    const hours = e.getHours().toString().padStart(2, "0");
    const minutes = e.getMinutes().toString().padStart(2, "0");
    const formattedDateStr = `${year}/${month}/${day} ${hours}:${minutes}:00`;

    setPostContents((prev) => ({
      ...prev,
      scheduled_at: formattedDateStr,
    }));
  };

  const handleChangeIsAttachedFile = (e: ChangeEvent<HTMLInputElement>) => {
    setPostContents((prev) => ({
      ...prev,
      is_attached_file: e.target.checked ? 1 : 0,
    }));
  };

  const [snackBarstatus, setSnackBarstatus] = useState<SnackbarData>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [confirmPopoverOpen, setPopoverSplitOpen] = useState(false);

  const postDisaster = async (isSuccess: boolean) => {
    if (!isSuccess) {
      console.log("何も行いません");
      return;
    }

    try {
      const status = await createDisasterManualTrigger(postContents);
      console.log("SS情報登録", status);
      setSnackBarstatus(status);
      setSnackbarOpen(true);

      if (onRegistrationSuccess) {
        onRegistrationSuccess();
      }
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  const putPostButtonClick = () => {
    setPopoverSplitOpen(true);
  };

  const confirmPopupClose = () => {
    setPopoverSplitOpen(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "fit-content",
        justifyContent: "center",
        gap: "16px",
        paddingRight: "40px",
        paddingLeft: "40px",
        "@media screen and (max-width:900px)": {
          padding: "16px",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          width: "100%",
          gap: "16px",
          "@media screen and (max-width:900px)": {
            flexDirection: "column",
          },
        }}
      >
        <PrefectureSelect
          changedValue={(value) => handleChangePref(value)}
          isMultiple={true}
        />
        <DivisionSelect
          prefCode={postContents.pref_code as string[] | null}
          changedValue={(value) => handleChangeDivision(value)}
        />
        <SsSelect
          prefCode={postContents.pref_code as string[] | null}
          divisionCode={postContents.m_division_code ?? null}
          changedValue={(value) => handleChangeSites(value)}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: "min-content",
          fontSize: 16,
          fontWeight: "bold",
          color: "#000",
          whiteSpace: "nowrap",
          "@media screen and (max-width:900px)": {
            flexDirection: "column",
            width: "100%",
            alignItems: "flex-start",
          },
        }}
      >
        <Box sx={{ display: "flex", width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid #CCC",
              borderRight: "none",
              borderTopLeftRadius: "4px",
              borderBottomLeftRadius: "4px",
              backgroundColor: "outline.light",
              width: "112px",
            }}
          >
            追加文章
          </Box>
          <TextField
            name="appendTextField"
            id="outlined-basic"
            variant="outlined"
            value={postContents.append_text}
            onChange={handleChangeAdditionalText}
            sx={{
              height: "40px",
              width: "100%",
              ".MuiInputBase-root": {
                height: "100%",
              },
              "#outlined-basic": {
                boxSizing: "border-box",
                height: "100%",
              },
              ".MuiOutlinedInput-root": {
                "& fieldset": {
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                },
              },
            }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: "min-content",
          fontSize: 16,
          fontWeight: "bold",
          color: "#000",
          whiteSpace: "nowrap",
          "@media screen and (max-width:900px)": {
            flexDirection: "column",
            width: "100%",
            alignItems: "flex-start",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            minWidth: "160px",
          }}
        >
          実行予定日時
        </Box>
        <Box sx={{ display: "flex", gap: "8px", width: "100%", alignItems: "center", flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Checkbox
              name="immediateCheckbox"
              size="small"
              checked={postContents.scheduled_at === null}
              onClick={(e) => e.stopPropagation()}
              onChange={handleChangeImmediate}
              sx={{ cursor: "pointer", pointerEvents: "auto" }}
            />
            即時
          </Box>
          <DatePickerJapanese
            type="datetime"
            inputFormat="yyyy-MM-dd"
            placeholder="****年**月**日"
            value={
              postContents.scheduled_at
                ? new Date(postContents.scheduled_at)
                : null
            }
            onChange={handleChangeScheduledAt}
            column="scheduled_at"
          />
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Checkbox
              name="recoveryChecklistAttachCheckbox"
              size="small"
              checked={postContents.is_attached_file === 1}
              onClick={(e) => e.stopPropagation()}
              onChange={handleChangeIsAttachedFile}
              sx={{ cursor: "pointer", pointerEvents: "auto" }}
            />
            SS復旧チェックリスト添付する
          </Box>
        </Box>
      </Box>
      <Button
        name="postButton"
        variant="contained"
        onClick={putPostButtonClick}
        sx={{ width: "48px" }}
      >
        登録
      </Button>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          minWidth: "min-content",
          paddingLeft: "40px",
          fontSize: 16,
          fontWeight: "bold",
          color: "#000",
          whiteSpace: "nowrap",
          "@media screen and (max-width:900px)": {
            padding: "0",
          },
        }}
      >
        災害手動登録準備中
      </Box>
      <SnackbarCustom
        data={snackBarstatus}
        openStatus={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
      />
      <PopoverCustom
        open={confirmPopoverOpen}
        onClose={confirmPopupClose}
        yesNo={(value) => postDisaster(value)}
        title={"災害手動登録確認"}
        contents={"登録してもよろしいですか？"}
        locatorName={"disaster_manual_registration_confirmation"}
      />
    </Box>
  );
};

export default ManualRegistration;
